/**
 * RealDataMatrix - Generates REAL scannable GS1 DataMatrix barcodes
 * Uses bwip-js library for proper ECC200 encoding (ISO/IEC 16022)
 *
 * IMPORTANT: This replaces the fake hash-based DataMatrix generator
 * The old generator created visual patterns that looked like DataMatrix
 * but could NOT be decoded by any scanner.
 */
import React, { useEffect, useRef, useState } from 'react';
// Use explicit browser subpath export for consistent resolution in all environments
// The default 'bwip-js' import resolves differently in node vs browser conditions
import bwipjs from 'bwip-js/browser';

interface RealDataMatrixProps {
  /** GS1 data string WITH parentheses (e.g., "(01)07890000000086(17)261119(10)LOT123") */
  data: string;
  /** Size in pixels (width and height) */
  size?: number;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
  /** Show data string below the barcode */
  showData?: boolean;
}

/**
 * Generates a REAL GS1 DataMatrix barcode that can be scanned
 *
 * GS1 DataMatrix Rules:
 * - FNC1 is automatically added at the start by bwip-js when using 'gs1datamatrix'
 * - Data should NOT contain parentheses (those are human notation only)
 * - FNC1 separators between variable-length fields are handled by bwip-js
 *
 * Example:
 * Human Readable: (01)07890000000086(17)261119(10)LOT123
 * Barcode Data:   0107890000000086172611910LOT123
 */
export const RealDataMatrix: React.FC<RealDataMatrixProps> = ({
  data,
  size = 150,
  onError,
  showData = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!canvasRef.current || !data) {
      setIsReady(false);
      return;
    }

    setError(null);
    setIsReady(false);

    try {
      // Calculate scale based on desired size
      // DataMatrix modules are typically small, so we scale up
      const scale = Math.max(2, Math.floor(size / 50));

      bwipjs.toCanvas(canvasRef.current, {
        bcid: 'gs1datamatrix',  // GS1 DataMatrix - automatically handles FNC1
        text: data,
        scale: scale,
        includetext: false,    // Don't include text in the barcode image
        padding: 2,            // Small quiet zone
      });

      setIsReady(true);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      console.error('[RealDataMatrix] Encoding error:', err.message);
      setError(err.message);
      onError?.(err);
    }
  }, [data, size, onError]);

  if (!data) {
    return (
      <div
        className="flex items-center justify-center bg-gray-100 rounded border border-gray-200"
        style={{ width: size, height: size }}
      >
        <span className="text-xs text-gray-400">No data</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center bg-red-50 rounded border border-red-200 p-2"
        style={{ width: size, minHeight: size / 2 }}
      >
        <span className="text-xs text-red-500 text-center">
          Erro: {error}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div
        className="bg-white p-1 rounded border border-gray-200"
        style={{ maxWidth: size, maxHeight: size }}
      >
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            display: isReady ? 'block' : 'none'
          }}
        />
        {!isReady && (
          <div
            className="flex items-center justify-center"
            style={{ width: size - 10, height: size - 10 }}
          >
            <span className="text-xs text-gray-400">Loading...</span>
          </div>
        )}
      </div>
      {showData && data && (
        <div className="mt-1 text-[8px] text-gray-500 font-mono max-w-full truncate" style={{ maxWidth: size }}>
          {data}
        </div>
      )}
    </div>
  );
};

/**
 * Compact version for table cells
 * Same as RealDataMatrix but with smaller default size
 */
export const CompactRealDataMatrix: React.FC<{
  data: string;
  size?: number;
}> = ({ data, size = 80 }) => {
  return <RealDataMatrix data={data} size={size} showData={false} />;
};

/**
 * Helper function to format GS1 data for DataMatrix encoding
 *
 * Input formats accepted:
 * - Human readable: (01)07890000000086(17)261119(10)LOT123
 *
 * Output: Same string with parentheses - bwip-js gs1datamatrix needs them to identify AIs
 */
export const formatGS1ForDataMatrix = (humanReadable: string): string => {
  // KEEP parentheses - bwip-js gs1datamatrix needs them to identify AIs
  return humanReadable;
};

/**
 * Pad GTIN to 14 digits (GTIN-14 format required for GS1 DataMatrix)
 * Preserves original checksum when possible
 */
export const padGTIN14 = (gtin: string): string => {
  const cleaned = gtin.replace(/\D/g, '');

  // Already 14 digits
  if (cleaned.length === 14) return cleaned;

  // 13 digits (EAN-13) -> add leading zero
  if (cleaned.length === 13) return '0' + cleaned;

  // Pad shorter GTINs to 14 digits
  return cleaned.padStart(14, '0').slice(0, 14);
};
